import { useCallback, useEffect, useRef, useState } from 'react'
import { connectPush, supportsPush } from './pushClient'

export function usePushNotifications(token) {
    const [status, setStatus] = useState('checking')
    const [error, setError] = useState('')
    const tokenRef = useRef(token)

    useEffect(() => {
        tokenRef.current = token
        return () => { tokenRef.current = null }
    }, [token])

    const synchronize = useCallback(async () => {
        if (!supportsPush()) { setStatus('unsupported'); return }
        if (!token) { setStatus('not_connected'); return }
        if (Notification.permission !== 'granted') {
            setStatus(Notification.permission === 'denied' ? 'blocked' : 'not_connected')
            return
        }
        setStatus('checking')
        setError('')
        try {
            const connected = await connectPush(token)
            if (tokenRef.current === token) setStatus(connected ? 'connected' : 'not_connected')
        } catch (cause) {
            if (tokenRef.current !== token) return
            setStatus('error')
            setError(cause.name === 'AbortError' ? 'Сервер не ответил. Попробуйте ещё раз.'
                : cause instanceof TypeError ? 'Проверьте подключение к интернету и повторите'
                    : cause.message || 'Не удалось подключить уведомления')
        }
    }, [token])

    useEffect(() => {
        let disposed = false
        Promise.resolve().then(() => { if (!disposed) synchronize() })
        const onVisible = () => { if (document.visibilityState === 'visible') synchronize() }
        window.addEventListener('online', synchronize)
        document.addEventListener('visibilitychange', onVisible)
        const onMessage = event => { if (event.data?.type === 'push-subscription-changed') synchronize() }
        navigator.serviceWorker?.addEventListener('message', onMessage)
        return () => {
            disposed = true
            window.removeEventListener('online', synchronize)
            document.removeEventListener('visibilitychange', onVisible)
            navigator.serviceWorker?.removeEventListener('message', onMessage)
        }
    }, [synchronize])

    const requestPermission = async () => {
        if (!supportsPush()) { setStatus('unsupported'); return }
        try {
            if (Notification.permission === 'default') await Notification.requestPermission()
            await synchronize()
        } catch {
            setStatus('error')
            setError('Не удалось получить разрешение браузера')
        }
    }

    return { status, error, requestPermission }
}
